import * as Yup from 'yup';
import dayjs from 'dayjs';
import { ANY_OF_THOSE, IS_BETWEEN } from '../../../constants/filter';
import { formatPhoneInput } from './List/AddContactModal/helpers';

export const NOTE_EVENT_TYPES = [
  { value: 'call', label: 'Call' },
  { value: 'email', label: 'Email' },
  { value: 'meeting', label: 'Meeting' },
  { value: 'review', label: 'Review' },
  { value: 'reminder', label: 'Reminder' },
  { value: 'site_visit', label: 'Site Visit' },
  { value: 'other', label: 'Other' },
];

export const getContactDisplayName = (contact) => {
  if (!contact) return '-';

  const name = [contact.firstName, contact.lastName].filter(Boolean).join(' ').trim();
  return name || contact.email || '-';
};

export const CONTACT_STATUS_FILTERS = {
  all: null,
  active: { data: ['active'], type: ANY_OF_THOSE },
  archived: { data: ['archived'], type: ANY_OF_THOSE },
};

export const getContactStatusView = (fields = {}) => {
  const statusData = fields.status?.data || [];

  if (!statusData.length) {
    return 'all';
  }

  if (statusData.includes('archived') && !statusData.includes('active')) {
    return 'archived';
  }

  if (statusData.includes('active') && !statusData.includes('archived')) {
    return 'active';
  }

  return 'all';
};

export const getContactTypeView = (fields = {}) => {
  const typeData = fields.contactType?.data || [];

  if (typeData.includes('agent')) {
    return 'agent';
  }

  if (typeData.includes('vendor')) {
    return 'vendor';
  }

  return 'all';
};

export const CONTACT_ORIGIN_OTHER = 'other';

export const CONTACT_ORIGINS = [
  { value: 'referrals', label: 'Referrals' },
  { value: 'usfcr', label: 'USFCR' },
  { value: 'professional_associations', label: 'Professional Associations' },
  { value: 'conferences', label: 'Conferences' },
  { value: 'competitor', label: 'Competitor' },
  { value: 'supplier_portal', label: 'Supplier Portal' },
  { value: 'other', label: 'Other' },
];

export const CONTACT_SELECTABLE_ORIGINS = CONTACT_ORIGINS.filter((item) => item.value !== 'usfcr');

export const CONTACT_ORIGIN_FILTERS = {
  all: null,
  referrals: { data: ['referrals'], type: ANY_OF_THOSE },
  usfcr: { data: ['usfcr'], type: ANY_OF_THOSE },
  professional_associations: { data: ['professional_associations'], type: ANY_OF_THOSE },
  conferences: { data: ['conferences'], type: ANY_OF_THOSE },
  competitor: { data: ['competitor'], type: ANY_OF_THOSE },
  supplier_portal: { data: ['supplier_portal'], type: ANY_OF_THOSE },
  other: { data: ['other'], type: ANY_OF_THOSE },
};

export const getContactOriginView = (fields = {}) => {
  const originData = fields.origin?.data || [];

  if (originData.length === 1) {
    return originData[0];
  }

  return 'all';
};

export const getContactOriginLabel = (origin) => {
  const match = CONTACT_ORIGINS.find((item) => item.value === origin);
  return match?.label || formatLabel(origin);
};

export const getContactOriginDisplay = (contact = {}) => {
  const label = getContactOriginLabel(contact.origin);

  if (contact.origin === CONTACT_ORIGIN_OTHER && contact.originDetail) {
    return `${label} — ${contact.originDetail}`;
  }

  return label;
};

export const DEFAULT_CONTACT_STAGE = 'New';
export const DEFAULT_CONTACT_DISPOSITION = 'New';

export const getNoteInitialValues = (contact = {}) => {
  const isMonitoring = ['Monitoring as Client', 'Monitoring as Competitor'].includes(contact?.stage);

  return {
    crmContactId: contact?.id || '',
    title: '',
    eventDate: dayjs().startOf('day').toISOString(),
    eventTime: dayjs().format('HH:mm'),
    eventType: '',
    content: '',
    nextStep: '',
    additionalDetails: '',
    stage: contact?.stage || DEFAULT_CONTACT_STAGE,
    disposition: isMonitoring ? contact?.disposition || '' : contact?.disposition || DEFAULT_CONTACT_DISPOSITION,
  };
};

export const buildNoteNextStep = (stage, disposition) => {
  const parts = [stage, disposition].map((part) => String(part || '').trim()).filter(Boolean);
  return parts.length ? parts.join(' / ') : null;
};

export const buildNoteEventDate = (eventDate, eventTime) => {
  if (!eventDate) return null;

  const datePart = dayjs(eventDate).format('YYYY-MM-DD');
  if (!eventTime) {
    return dayjs(datePart).toISOString();
  }

  return dayjs(`${datePart}T${eventTime}`).toISOString();
};

const NOTE_EVENT_TYPE_VALUES = NOTE_EVENT_TYPES.map((item) => item.value);

export const noteFormSchema = (trans) =>
  Yup.object().shape({
    crmContactId: Yup.string().required(trans('required-field')),
    title: Yup.string().nullable(),
    eventDate: Yup.string().nullable(),
    eventTime: Yup.string().nullable(),
    eventType: Yup.string().oneOf(NOTE_EVENT_TYPE_VALUES).required(trans('required-field')),
    content: Yup.string().required(trans('required-field')),
    nextStep: Yup.string().nullable(),
    additionalDetails: Yup.string().nullable(),
    stage: Yup.string()
      .oneOf([...CONTACT_STAGES, ''])
      .nullable(),
    disposition: Yup.string()
      .oneOf([...CONTACT_DISPOSITIONS, ''])
      .nullable(),
  });

export const orderOptions = [
  { name: 'first-name', value: 'firstName', asc: 'A-Z', desc: 'Z-A' },
  { name: 'last-name', value: 'lastName', asc: 'A-Z', desc: 'Z-A' },
  { name: 'email', value: 'email', asc: 'A-Z', desc: 'Z-A' },
  { name: 'phone', value: 'primaryPhone', asc: 'A-Z', desc: 'Z-A' },
  { name: 'organization', value: 'companyName', asc: 'A-Z', desc: 'Z-A' },
  { name: 'title', value: 'title', asc: 'A-Z', desc: 'Z-A' },
  { name: 'type', value: 'contactType', asc: 'A-Z', desc: 'Z-A' },
  { name: 'stage', value: 'stage', asc: 'A-Z', desc: 'Z-A' },
  { name: 'disposition', value: 'disposition', asc: 'A-Z', desc: 'Z-A' },
  { name: 'status', value: 'status', asc: 'A-Z', desc: 'Z-A' },
  { name: 'last-contacted', value: 'lastContacted', asc: 'oldest-first', desc: 'newest-first' },
  { name: 'source', value: 'source', asc: 'A-Z', desc: 'Z-A' },
  { name: 'comments', value: 'comments', asc: 'A-Z', desc: 'Z-A' },
  { name: 'set-asides', value: 'setAsides', asc: 'A-Z', desc: 'Z-A' },
  { name: 'company-size', value: 'companySize', asc: 'A-Z', desc: 'Z-A' },
  { name: 'updated', value: 'updatedAt', asc: 'oldest-first', desc: 'newest-first' },
];

export const CRM_ONBOARDING_REPLAY_EVENT = 'govcrm-onboarding-replay';
export const CRM_ONBOARDING_ACTIVE_EVENT = 'govcrm-onboarding-active';
export const CRM_ONBOARDING_STORAGE_PREFIX = 'govcrm_onboarding_completed';

export const getCrmOnboardingStorageKey = (userKey) => `${CRM_ONBOARDING_STORAGE_PREFIX}_${userKey}`;

export const isCrmOnboardingCompleted = (userKey) => {
  if (!userKey || typeof window === 'undefined') {
    return true;
  }

  return window.localStorage.getItem(getCrmOnboardingStorageKey(userKey)) === '1';
};

export const markCrmOnboardingCompleted = (userKey) => {
  if (!userKey || typeof window === 'undefined') {
    return;
  }

  window.localStorage.setItem(getCrmOnboardingStorageKey(userKey), '1');
};

export const CRM_TAB_KEYS = {
  DASHBOARD: 'dashboard',
  CONTACTS: 'contacts',
  ORGANIZATIONS: 'organizations',
  NOTES: 'notes',
  ACTIVITY: 'activity',
  REFERENCE_ITEMS: 'reference-items',
};

export const CRM_WEEKLY_FILTER_TYPES = {
  CONTACTS_ADDED: 'contacts-added',
  ORGANIZATIONS_ADDED: 'organizations-added',
  CONTACTS_TOUCHED: 'contacts-touched',
  NOTES_THIS_WEEK: 'notes-this-week',
};

export const getCrmWeekWindow = (referenceDate = dayjs()) => {
  const now = dayjs(referenceDate);
  const weekStart = now.subtract(now.day(), 'day').startOf('day');
  const weekEnd = now.endOf('day');

  return {
    weekStart: weekStart.toISOString(),
    weekEnd: weekEnd.toISOString(),
    weekStartLabel: weekStart.format('ddd, MMM D'),
  };
};

const buildWeeklyDateRange = (weekWindow) => ({
  type: IS_BETWEEN,
  from: weekWindow.weekStart,
  to: weekWindow.weekEnd,
});

/** Full contact list filter reset — every field key must be set so stale filters are cleared. */
export const buildFreshContactFilters = (overrides = {}) => ({
  text: '',
  reset: true,
  status: overrides.status ?? CONTACT_STATUS_FILTERS.active,
  contactType: overrides.contactType ?? null,
  stage: overrides.stage ?? null,
  disposition: overrides.disposition ?? null,
  source: overrides.source ?? null,
  origin: overrides.origin ?? null,
  crmCompanyId: overrides.crmCompanyId ?? null,
  createdAt: overrides.createdAt ?? null,
  touchedSince: overrides.touchedSince ?? null,
});

/** Full organization list filter reset — every field key must be set so stale filters are cleared. */
export const buildFreshOrganizationFilters = (overrides = {}) => ({
  text: '',
  reset: true,
  status: overrides.status ?? CONTACT_STATUS_FILTERS.active,
  companyType: overrides.companyType ?? null,
  createdAt: overrides.createdAt ?? null,
});

/** Full notes list filter reset. */
export const buildFreshNotesFilters = (overrides = {}) => ({
  text: '',
  reset: true,
  eventDate: overrides.eventDate ?? null,
});

export const buildWeeklyContactFilters = (filterType, weekWindow) => {
  const dateRange = buildWeeklyDateRange(weekWindow);

  if (filterType === CRM_WEEKLY_FILTER_TYPES.CONTACTS_ADDED) {
    return buildFreshContactFilters({ createdAt: dateRange });
  }

  if (filterType === CRM_WEEKLY_FILTER_TYPES.CONTACTS_TOUCHED) {
    return buildFreshContactFilters({
      status: { data: ['active', 'archived'], type: ANY_OF_THOSE },
      touchedSince: dateRange,
    });
  }

  return buildFreshContactFilters();
};

export const buildWeeklyOrganizationFilters = (weekWindow) =>
  buildFreshOrganizationFilters({ createdAt: buildWeeklyDateRange(weekWindow) });

export const buildWeeklyNotesFilters = (weekWindow) =>
  buildFreshNotesFilters({ eventDate: buildWeeklyDateRange(weekWindow) });

export const getDefaultContactListFilters = () => buildFreshContactFilters();

export const getDefaultOrganizationListFilters = () => buildFreshOrganizationFilters();

export const getWeeklyFilterMessage = (filterType, weekStartLabel) => {
  const sinceLabel = `Since ${weekStartLabel}`;

  switch (filterType) {
    case CRM_WEEKLY_FILTER_TYPES.CONTACTS_ADDED:
      return `Showing contacts added ${sinceLabel.toLowerCase()}`;
    case CRM_WEEKLY_FILTER_TYPES.ORGANIZATIONS_ADDED:
      return `Showing organizations added ${sinceLabel.toLowerCase()}`;
    case CRM_WEEKLY_FILTER_TYPES.CONTACTS_TOUCHED:
      return `Showing contacts touched ${sinceLabel.toLowerCase()}`;
    case CRM_WEEKLY_FILTER_TYPES.NOTES_THIS_WEEK:
      return `Showing notes ${sinceLabel.toLowerCase()}`;
    default:
      return `Showing records ${sinceLabel.toLowerCase()}`;
  }
};

export const CRM_KPI_CARDS = [
  {
    key: CRM_WEEKLY_FILTER_TYPES.CONTACTS_ADDED,
    label: 'Contacts Added This Week',
    metricKey: 'contactsAdded',
    tabKey: CRM_TAB_KEYS.CONTACTS,
    icon: 'contacts',
  },
  {
    key: CRM_WEEKLY_FILTER_TYPES.ORGANIZATIONS_ADDED,
    label: 'Organizations Added This Week',
    metricKey: 'organizationsAdded',
    tabKey: CRM_TAB_KEYS.ORGANIZATIONS,
    icon: 'organizations',
  },
  {
    key: CRM_WEEKLY_FILTER_TYPES.CONTACTS_TOUCHED,
    label: 'Contacts Touched This Week',
    metricKey: 'contactsTouched',
    tabKey: CRM_TAB_KEYS.CONTACTS,
    icon: 'touched',
  },
  {
    key: CRM_WEEKLY_FILTER_TYPES.NOTES_THIS_WEEK,
    label: 'Notes This Week',
    metricKey: 'notesThisWeek',
    tabKey: CRM_TAB_KEYS.NOTES,
    icon: 'notes',
  },
];

export const CRM_ONBOARDING_STEPS = [
  {
    tabKey: CRM_TAB_KEYS.DASHBOARD,
    titleKey: 'crm-onboarding-dashboard-title',
    descriptionKey: 'crm-onboarding-dashboard-description',
  },
  {
    tabKey: CRM_TAB_KEYS.CONTACTS,
    titleKey: 'crm-onboarding-contacts-title',
    descriptionKey: 'crm-onboarding-contacts-description',
  },
  {
    tabKey: CRM_TAB_KEYS.ORGANIZATIONS,
    titleKey: 'crm-onboarding-organizations-title',
    descriptionKey: 'crm-onboarding-organizations-description',
  },
  {
    tabKey: CRM_TAB_KEYS.NOTES,
    titleKey: 'crm-onboarding-notes-title',
    descriptionKey: 'crm-onboarding-notes-description',
  },
  {
    tabKey: CRM_TAB_KEYS.ACTIVITY,
    titleKey: 'crm-onboarding-activity-title',
    descriptionKey: 'crm-onboarding-activity-description',
  },
  {
    tabKey: CRM_TAB_KEYS.REFERENCE_ITEMS,
    titleKey: 'crm-onboarding-reference-items-title',
    descriptionKey: 'crm-onboarding-reference-items-description',
  },
];

export const CRM_ONBOARDING_DUMMY_TAB_KEYS = new Set([
  CRM_TAB_KEYS.DASHBOARD,
  CRM_TAB_KEYS.CONTACTS,
  CRM_TAB_KEYS.ORGANIZATIONS,
  CRM_TAB_KEYS.NOTES,
  CRM_TAB_KEYS.ACTIVITY,
]);

export const CRM_ONBOARDING_TOUR_USER_ID = 'crm-tour-user';

export const CRM_ONBOARDING_DUMMY_DATA = {
  filters: { text: '', fields: {} },
  pagination: { page: 1, perPage: 10 },
  dashboard: {
    loaded: true,
    breakdownsLoaded: true,
    contactsAdded: 3,
    organizationsAdded: 1,
    contactsTouched: 2,
    notesThisWeek: 4,
    byStage: [
      { key: 'New', count: 4 },
      { key: 'Initial outreach', count: 3 },
      { key: 'Connected', count: 2 },
      { key: 'Building relationship', count: 1 },
    ],
    byOrigin: [
      { key: 'conferences', count: 3 },
      { key: 'referrals', count: 2 },
      { key: 'professional_associations', count: 2 },
    ],
    byType: [
      { key: 'vendor', count: 5 },
      { key: 'agent', count: 2 },
    ],
    lastTouch: {
      items: [
        {
          id: 'crm-tour-contact-3',
          firstName: 'Sam',
          lastName: 'Nguyen',
          contactType: 'vendor',
          lastContacted: null,
          company: { name: 'BrightPath Logistics' },
        },
        {
          id: 'crm-tour-contact-1',
          firstName: 'Alex',
          lastName: 'Rivera',
          contactType: 'agent',
          lastContacted: '2026-06-01T14:30:00.000Z',
          company: { name: 'Acme Federal Solutions' },
        },
        {
          id: 'crm-tour-contact-2',
          firstName: 'Jordan',
          lastName: 'Lee',
          contactType: 'vendor',
          lastContacted: '2026-05-28T10:00:00.000Z',
          company: { name: 'Department of Example' },
        },
      ],
      count: 3,
      order: ['lastContacted', 'asc'],
      pagination: { page: 1, perPage: 10 },
      pending: false,
      loaded: true,
    },
  },
  contacts: [
    {
      id: 'crm-tour-contact-1',
      firstName: 'Alex',
      lastName: 'Rivera',
      email: 'alex.rivera@example.gov',
      primaryPhone: '2025550142',
      title: 'Contracting Officer',
      contactType: 'agent',
      stage: 'Connected',
      disposition: 'Intro meeting held',
      status: 'active',
      source: 'manual',
      origin: 'conferences',
      lastContacted: '2026-06-01T14:30:00.000Z',
      assignedToName: 'Morgan Taylor',
      company: {
        name: 'Acme Federal Solutions',
        setAsides: ['8(a)', 'WOSB'],
        companySize: 'small',
      },
      latestNote: {
        eventDate: '2026-06-01T14:30:00.000Z',
        content: 'Discussed upcoming RFQ timeline and capability review.',
      },
    },
    {
      id: 'crm-tour-contact-2',
      firstName: 'Jordan',
      lastName: 'Lee',
      email: 'jordan.lee@example.com',
      primaryPhone: '7035550198',
      title: 'Business Development Manager',
      contactType: 'vendor',
      stage: 'Initial outreach',
      disposition: 'Cap statement sent',
      status: 'active',
      source: 'upload',
      origin: 'referrals',
      lastContacted: '2026-05-28T10:00:00.000Z',
      assignedToName: 'Christopher Davis',
      company: {
        name: 'Department of Example',
        companySize: 'large',
      },
      latestNote: {
        eventDate: '2026-05-28T10:00:00.000Z',
        content: 'Sent capability statement and NAICS list.',
      },
    },
  ],
  companies: [
    {
      id: 'crm-tour-company-1',
      name: 'Acme Federal Solutions',
      contactCount: 2,
      companyType: 'vendor',
      source: 'manual',
      ueiCode: 'ABCDEF123456',
      cageCode: '1A2B3',
      city: 'Arlington',
      state: 'VA',
      companySize: 'small',
      status: 'active',
      naics: '541512',
      psc: 'R499',
      setAsides: ['8(a)', 'WOSB', 'SDVOSB'],
      updatedAt: '2026-06-01T12:00:00.000Z',
    },
    {
      id: 'crm-tour-company-2',
      name: 'Department of Example',
      contactCount: 1,
      companyType: 'agency',
      source: 'app',
      city: 'Washington',
      state: 'DC',
      companySize: 'large',
      status: 'active',
      updatedAt: '2026-05-28T09:00:00.000Z',
    },
  ],
  notes: [
    {
      id: 'crm-tour-note-1',
      eventType: 'call',
      title: 'Follow-up call scheduled',
      content: 'Confirmed next steps and timeline for the capability review meeting.',
      eventDate: '2026-06-01T14:30:00.000Z',
      contact: {
        id: 'crm-tour-contact-1',
        firstName: 'Alex',
        lastName: 'Rivera',
      },
    },
    {
      id: 'crm-tour-note-2',
      eventType: 'email',
      title: 'Sent capability statement',
      content: 'Emailed updated capability statement, NAICS codes, and past performance summary.',
      eventDate: '2026-05-28T10:00:00.000Z',
      contact: {
        id: 'crm-tour-contact-2',
        firstName: 'Jordan',
        lastName: 'Lee',
      },
    },
  ],
  audit: [
    {
      id: 'crm-tour-audit-1',
      description: 'Created contact Alex Rivera',
      createdAt: '2026-06-01T12:00:00.000Z',
      userId: CRM_ONBOARDING_TOUR_USER_ID,
      user: { name: 'Sample', familyName: 'User', email: 'you@example.com' },
    },
    {
      id: 'crm-tour-audit-2',
      description: 'Updated contact Jordan Lee',
      createdAt: '2026-05-28T11:00:00.000Z',
      userId: 'crm-tour-user-2',
      user: { name: 'Taylor', familyName: 'Morgan', email: 'taylor@example.com' },
      metadata: {
        changes: [
          { field: 'stage', from: 'Initial outreach', to: 'Connected' },
          { field: 'disposition', from: 'Email sent', to: 'Cap statement sent' },
        ],
      },
    },
    {
      id: 'crm-tour-audit-3',
      description: 'Added a call note for Alex Rivera',
      createdAt: '2026-06-01T14:30:00.000Z',
      userId: CRM_ONBOARDING_TOUR_USER_ID,
      user: { name: 'Sample', familyName: 'User', email: 'you@example.com' },
      crmNoteId: 'crm-tour-note-1',
    },
  ],
  users: [
    {
      _id: CRM_ONBOARDING_TOUR_USER_ID,
      userName: 'crm-tour-user',
      name: 'Sample',
      familyName: 'User',
      email: 'you@example.com',
    },
    {
      _id: 'crm-tour-user-2',
      userName: 'taylor',
      name: 'Taylor',
      familyName: 'Morgan',
      email: 'taylor@example.com',
    },
  ],
};

export const PLACEHOLDER_TABS = [
  { key: CRM_TAB_KEYS.DASHBOARD, label: 'Dashboard', disabled: false },
  { key: CRM_TAB_KEYS.CONTACTS, label: 'Contacts', disabled: false },
  { key: CRM_TAB_KEYS.ORGANIZATIONS, label: 'Organizations', disabled: false },
  { key: CRM_TAB_KEYS.NOTES, label: 'Notes', disabled: false },
  { key: CRM_TAB_KEYS.ACTIVITY, label: 'Activity', disabled: false },
  { key: CRM_TAB_KEYS.REFERENCE_ITEMS, label: 'Reference Items', disabled: false },
];

export const ORGANIZATION_PINNED_LEFT_COLUMN_IDS = ['name'];
export const ORGANIZATION_ACTIONS_COLUMN_WIDTH = 120;

export const getOrganizationPinnedLeftOffset = (visibleColumns, columnId) => {
  const pinnedVisible = visibleColumns.filter((column) => ORGANIZATION_PINNED_LEFT_COLUMN_IDS.includes(column.id));
  const columnIndex = pinnedVisible.findIndex((column) => column.id === columnId);

  if (columnIndex === -1) {
    return null;
  }

  return pinnedVisible.slice(0, columnIndex).reduce((offset, column) => offset + (column.minWidth || 0), 0);
};

export const isOrganizationPinnedLeftColumn = (columnId) => ORGANIZATION_PINNED_LEFT_COLUMN_IDS.includes(columnId);

export const isLastOrganizationPinnedLeftColumn = (visibleColumns, columnId) => {
  const pinnedVisible = visibleColumns.filter((column) => ORGANIZATION_PINNED_LEFT_COLUMN_IDS.includes(column.id));

  return pinnedVisible[pinnedVisible.length - 1]?.id === columnId;
};

export const ORGANIZATION_LIST_COLUMNS = [
  { id: 'name', label: 'organization', orderLabel: 'name', minWidth: 220, required: true, pin: 'left' },
  { id: 'contactCount', label: 'contacts', orderLabel: 'contactCount', minWidth: 110 },
  { id: 'companyType', label: 'organization-type', orderLabel: 'companyType', minWidth: 140 },
  { id: 'source', label: 'source', orderLabel: 'source', minWidth: 130 },
  { id: 'ueiCode', label: 'uei', orderLabel: 'ueiCode', minWidth: 160 },
  { id: 'cageCode', label: 'cage', orderLabel: 'cageCode', minWidth: 130 },
  { id: 'city', label: 'city', orderLabel: 'city', minWidth: 140 },
  { id: 'state', label: 'state', orderLabel: 'state', minWidth: 100 },
  { id: 'companySize', label: 'company-size', orderLabel: 'companySize', minWidth: 150 },
  { id: 'status', label: 'status', orderLabel: 'status', minWidth: 130 },
  { id: 'naics', label: 'naics', orderLabel: 'naics', minWidth: 110 },
  { id: 'psc', label: 'psc', orderLabel: 'psc', minWidth: 110 },
  { id: 'setAsides', label: 'set-asides', orderLabel: 'setAsides', minWidth: 160, sortable: false },
  { id: 'updatedAt', label: 'updated', orderLabel: 'updatedAt', minWidth: 140 },
];

export const DEFAULT_ORGANIZATION_LIST_COLUMNS = [
  'name',
  'contactCount',
  'companyType',
  'source',
  'ueiCode',
  'cageCode',
  'city',
  'state',
  'companySize',
  'status',
  'setAsides',
  'updatedAt',
];

export const COMPANY_TYPES = ['vendor', 'agency'];

/** Identity/registry fields managed by the platform for site-synced organizations. */
export const COMPANY_SITE_LOCKED_FIELDS = [
  'name',
  'companyType',
  'ueiCode',
  'cageCode',
  'naics',
  'psc',
  'address',
  'city',
  'state',
  'zipCode',
];

export const isCompanySiteSynced = (company = {}) => company.companyType === 'agency' || company.source === 'site';

export const isCompanyFieldEditable = (company, field) =>
  !isCompanySiteSynced(company) || !COMPANY_SITE_LOCKED_FIELDS.includes(field);

export const COMPANY_SIZE_FILTERS = {
  all: null,
  small: { data: ['small'], type: ANY_OF_THOSE },
  medium: { data: ['medium'], type: ANY_OF_THOSE },
  large: { data: ['large'], type: ANY_OF_THOSE },
};

export const COMPANY_TYPE_FILTERS = {
  all: null,
  vendor: { data: ['vendor'], type: ANY_OF_THOSE },
  agency: { data: ['agency'], type: ANY_OF_THOSE },
};

export const COMPANY_SIZES = ['small', 'medium', 'large'];

const optionalCompanyEmail = Yup.string()
  .transform((value) => (value === '' ? null : value))
  .email('Please enter a valid email address')
  .nullable();

const optionalCompanyPhone = Yup.string()
  .transform((value) => (value === '' ? null : value))
  .nullable()
  .test('company-phone-format', 'Please enter a valid 10-digit phone number', (value) => {
    if (!value) {
      return true;
    }

    const digits = String(value).replaceAll(/\D/g, '');
    const normalized = digits.length === 11 && digits.startsWith('1') ? digits.substring(1) : digits;
    return normalized.length === 10;
  });

export const companyEditSchema = () =>
  Yup.object().shape({
    name: Yup.string().nullable(),
    ueiCode: Yup.string().nullable(),
    cageCode: Yup.string().nullable(),
    address: Yup.string().nullable(),
    city: Yup.string().nullable(),
    state: Yup.string().nullable(),
    zipCode: Yup.string().nullable(),
    naics: Yup.string().nullable(),
    psc: Yup.string().nullable(),
    companySize: Yup.string().oneOf(COMPANY_SIZES).nullable(),
    companyType: Yup.string().oneOf(COMPANY_TYPES).nullable(),
    organizationNotes: Yup.string().nullable(),
    agencyOffice: Yup.string().nullable(),
    companyEmail: optionalCompanyEmail,
    companyPhone: optionalCompanyPhone,
  });

export const getCompanyEditInitialValues = (company = {}) => ({
  name: company.name || '',
  ueiCode: company.ueiCode || '',
  cageCode: company.cageCode || '',
  address: company.address || '',
  city: company.city || '',
  state: company.state || '',
  zipCode: company.zipCode || '',
  naics: company.naics || '',
  psc: company.psc || '',
  companySize: company.companySize || '',
  companyType: company.companyType || 'vendor',
  organizationNotes: company.organizationNotes || '',
  agencyOffice: company.agencyOffice || '',
  companyEmail: company.companyEmail || '',
  companyPhone: formatPhoneInput(company.companyPhone || ''),
});

export const getCompanySizeView = (fields = {}) => {
  const sizeData = fields.companySize?.data || [];

  if (sizeData.includes('small')) return 'small';
  if (sizeData.includes('medium')) return 'medium';
  if (sizeData.includes('large')) return 'large';

  return 'all';
};

export const getCompanyTypeView = (fields = {}) => {
  const typeData = fields.companyType?.data || [];

  if (typeData.includes('vendor')) return 'vendor';
  if (typeData.includes('agency')) return 'agency';

  return 'all';
};

/** Full pipeline stage vocabulary shown in the reference guide (matches CONTACT_STAGES / backend CRM_CONTACT_STAGES). */
export const REFERENCE_PIPELINE_STAGES = [
  'New',
  'Initial outreach',
  'Connected',
  'Building relationship',
  'Active partnership',
  'Maintenance',
  'Monitoring as Client',
  'Monitoring as Competitor',
];

/** Stage-to-disposition mapping (aligned with api/crmContacts/constants.js). */
export const CRM_STAGE_DISPOSITIONS = {
  New: ['New'],
  'Initial outreach': ['Email sent', 'LM', 'LM2', 'LM3', 'No response', 'Bad contact'],
  Connected: ['Intro meeting scheduled', 'Intro meeting held', 'Cap statement sent'],
  'Building relationship': ['Follow up Scheduled', 'NDA sent', 'NDA signed', 'Supplier portal submitted', 'Site Visit'],
  'Active partnership': [
    'Added to approved vendor list',
    'Teaming discussion',
    'Teaming agreement signed',
    'Pursuing opportunity together',
  ],
  Maintenance: ['Nurture', 'Dormant'],
};

export const DISPOSITION_DESCRIPTIONS = {
  New: 'Contact recently added, not yet in active outreach',
  'Email sent': 'First email sent, awaiting response',
  LM: 'Left first voicemail',
  LM2: 'Left second voicemail',
  LM3: 'Left third voicemail, last attempt',
  'No response': 'No reply after multiple attempts',
  'Bad contact': 'Wrong POC, need to find new contact',
  'Intro meeting scheduled': 'Meeting on the calendar',
  'Intro meeting held': 'Initial conversation completed',
  'Cap statement sent': 'They asked for and received our cap statement',
  'Follow up Scheduled': 'Follow-up meeting or touchpoint scheduled',
  'NDA sent': 'NDA out for signature',
  'NDA signed': 'NDA fully executed',
  'Supplier portal submitted': 'Vendor application submitted',
  'Site Visit': 'On-site visit completed or scheduled',
  'Added to approved vendor list': 'Approved as a registered vendor',
  'Teaming discussion': 'Discussing teaming on a specific opportunity',
  'Teaming agreement signed': 'Teaming agreement fully executed',
  'Pursuing opportunity together': 'Actively pursuing an opportunity as a team',
  Nurture: 'Relationship maintained with periodic outreach',
  Dormant: 'No recent activity; relationship on hold',
};

export const STAGE_REFERENCE_COLORS = {
  New: '#ecfeff',
  'Initial outreach': '#e8f0fe',
  Connected: '#e8f5e9',
  'Building relationship': '#fffbeb',
  'Active partnership': '#f3e8ff',
  Maintenance: '#eef2f6',
  'Monitoring as Client': '#e0f2fe',
  'Monitoring as Competitor': '#fdf2f8',
};

export const getDispositionDescription = (disposition) => DISPOSITION_DESCRIPTIONS[disposition] || '';

export const buildPipelineReferenceRows = () => {
  const rows = [];

  REFERENCE_PIPELINE_STAGES.forEach((stage) => {
    const dispositions = CRM_STAGE_DISPOSITIONS[stage] || [];

    if (!dispositions.length) {
      rows.push({
        stage,
        disposition: null,
        description: 'No disposition applies to this stage',
        isFirstInStage: true,
      });
      return;
    }

    dispositions.forEach((disposition, index) => {
      rows.push({
        stage,
        disposition,
        description: getDispositionDescription(disposition),
        isFirstInStage: index === 0,
        stageRowSpan: dispositions.length,
      });
    });
  });

  return rows;
};

export const CONTACT_PINNED_LEFT_COLUMN_IDS = ['firstName', 'lastName'];
export const CONTACT_ACTIONS_COLUMN_WIDTH = 120;

export const getContactPinnedLeftOffset = (visibleColumns, columnId) => {
  const pinnedVisible = visibleColumns.filter((column) => CONTACT_PINNED_LEFT_COLUMN_IDS.includes(column.id));
  const columnIndex = pinnedVisible.findIndex((column) => column.id === columnId);

  if (columnIndex === -1) {
    return null;
  }

  return pinnedVisible.slice(0, columnIndex).reduce((offset, column) => offset + (column.minWidth || 0), 0);
};

export const isContactPinnedLeftColumn = (columnId) => CONTACT_PINNED_LEFT_COLUMN_IDS.includes(columnId);

export const isLastContactPinnedLeftColumn = (visibleColumns, columnId) => {
  const pinnedVisible = visibleColumns.filter((column) => CONTACT_PINNED_LEFT_COLUMN_IDS.includes(column.id));

  return pinnedVisible[pinnedVisible.length - 1]?.id === columnId;
};

export const CONTACT_LIST_COLUMNS = [
  { id: 'firstName', label: 'first-name', orderLabel: 'firstName', minWidth: 150, required: true, pin: 'left' },
  { id: 'lastName', label: 'last-name', orderLabel: 'lastName', minWidth: 150, required: true, pin: 'left' },
  { id: 'email', label: 'email', orderLabel: 'email', minWidth: 200 },
  { id: 'primaryPhone', label: 'phone', orderLabel: 'primaryPhone', minWidth: 160 },
  { id: 'company', label: 'organization', orderLabel: 'companyName', minWidth: 160 },
  { id: 'title', label: 'title', orderLabel: 'title', minWidth: 220 },
  { id: 'contactType', label: 'type', orderLabel: 'contactType', minWidth: 130 },
  { id: 'origin', label: 'origin', orderLabel: 'origin', minWidth: 180 },
  { id: 'stage', label: 'stage', orderLabel: 'stage', minWidth: 230 },
  { id: 'disposition', label: 'disposition', orderLabel: 'disposition', minWidth: 260 },
  { id: 'status', label: 'status', orderLabel: 'status', minWidth: 130 },
  { id: 'assigned', label: 'assigned', orderLabel: 'assignedTo', minWidth: 160 },
  { id: 'lastContacted', label: 'last-contacted', orderLabel: 'lastContacted', minWidth: 150 },
  { id: 'source', label: 'source', orderLabel: 'source', minWidth: 130 },
  { id: 'comments', label: 'comments', orderLabel: 'comments', minWidth: 130 },
  { id: 'setAsides', label: 'set-asides', orderLabel: 'setAsides', minWidth: 130 },
  { id: 'companySize', label: 'company-size', orderLabel: 'companySize', minWidth: 170 },
];

export const DEFAULT_CONTACT_LIST_COLUMNS = CONTACT_LIST_COLUMNS.map((column) => column.id);

export const CONTACT_TYPES = ['agent', 'vendor'];

export const CONTACT_STAGES = [
  'New',
  'Initial outreach',
  'Connected',
  'Building relationship',
  'Active partnership',
  'Maintenance',
  'Monitoring as Client',
  'Monitoring as Competitor',
];

export const CRM_DASHBOARD_CHARTS = [
  {
    key: 'byStage',
    title: 'Contacts by Stage',
    subtitle: 'Distribution across the pipeline',
    filterField: 'stage',
    sortKeys: CONTACT_STAGES,
    getLabel: (key) => key,
    accentKeys: ['New'],
  },
  {
    key: 'byOrigin',
    title: 'Contacts by Origin',
    subtitle: 'Breakdown by lead source',
    filterField: 'origin',
    sortKeys: CONTACT_ORIGINS.map((item) => item.value),
    getLabel: getContactOriginLabel,
    accentKeys: ['conferences'],
  },
  {
    key: 'byType',
    title: 'Contacts by Type',
    subtitle: 'Vendors vs. agents and other roles',
    filterField: 'contactType',
    sortKeys: ['vendor', 'agent'],
    getLabel: (key) => (key === 'vendor' ? 'Vendor' : key === 'agent' ? 'Agent' : key),
    accentKeys: [],
  },
];

const DASHBOARD_CHART_BLUE_SCALE = [
  '#1e3a8a',
  '#1d4ed8',
  '#2563eb',
  '#3b82f6',
  '#60a5fa',
  '#93c5fd',
  '#bfdbfe',
  '#dbeafe',
  '#e0e7ff',
];

const DASHBOARD_CHART_ACCENT_COLOR = '#a3c585';

export const getDashboardChartColor = (index, key, accentKeys = []) => {
  if (accentKeys.includes(key)) {
    return DASHBOARD_CHART_ACCENT_COLOR;
  }

  return DASHBOARD_CHART_BLUE_SCALE[index % DASHBOARD_CHART_BLUE_SCALE.length];
};

export const buildDashboardChartItems = (rows = [], chartConfig = {}) => {
  const { sortKeys = [], getLabel = (key) => key, accentKeys = [] } = chartConfig;
  const countByKey = rows.reduce((acc, row) => {
    acc[row.key] = row.count;
    return acc;
  }, {});

  const orderedKeys = [
    ...sortKeys.filter((key) => countByKey[key] > 0),
    ...Object.keys(countByKey).filter((key) => !sortKeys.includes(key)),
  ];

  return orderedKeys.map((key, index) => ({
    key,
    label: getLabel(key),
    value: countByKey[key],
    color: getDashboardChartColor(index, key, accentKeys),
  }));
};

export const CONTACT_DISPOSITIONS = [
  'New',
  'Email sent',
  'LM',
  'LM2',
  'LM3',
  'No response',
  'Bad contact',
  'Intro meeting scheduled',
  'Intro meeting held',
  'Cap statement sent',
  'Follow up Scheduled',
  'NDA sent',
  'NDA signed',
  'Supplier portal submitted',
  'Site Visit',
  'Added to approved vendor list',
  'Teaming discussion',
  'Teaming agreement signed',
  'Pursuing opportunity together',
  'Nurture',
  'Dormant',
];

export const MONITORING_CONTACT_STAGES = ['Monitoring as Client', 'Monitoring as Competitor'];

export const isMonitoringContactStage = (stage) => MONITORING_CONTACT_STAGES.includes(stage);

export const getDispositionOptionsForStage = (stage) => {
  if (!stage || isMonitoringContactStage(stage)) {
    return [];
  }

  return CRM_STAGE_DISPOSITIONS[stage] || [];
};

export const getDefaultDispositionForStage = (stage) => {
  if (!stage || isMonitoringContactStage(stage)) {
    return null;
  }

  const options = getDispositionOptionsForStage(stage);
  return options[0] || DEFAULT_CONTACT_DISPOSITION;
};

export const EDITABLE_CONTACT_FIELDS = [
  'firstName',
  'lastName',
  'title',
  'email',
  'secondaryEmail',
  'primaryPhone',
  'secondaryPhone',
  'contactType',
  'origin',
  'originDetail',
  'stage',
  'disposition',
];

export const formatLabel = (value) => {
  if (!value) return '-';

  return String(value)
    .replace(/[_-]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
    .replace(/\w\S*/g, (text) => text.charAt(0).toUpperCase() + text.substr(1).toLowerCase());
};

export const formatDate = (value) => {
  if (!value) return '-';

  const date = dayjs(value);
  if (!date.isValid()) return '-';

  return date.format('YYYY-MM-DD');
};

export const formatLastContactDate = (value) => {
  if (!value) return null;

  const date = dayjs(value);
  if (!date.isValid()) return null;

  return date.format('MMM D, YYYY');
};

export const CRM_DASHBOARD_LAST_TOUCH_COLUMNS = [
  { id: 'contact', label: 'Contact', orderLabel: 'lastName' },
  { id: 'company', label: 'Company', orderLabel: 'companyName' },
  { id: 'type', label: 'Type', orderLabel: 'contactType' },
  { id: 'lastContacted', label: 'Last Contact', orderLabel: 'lastContacted' },
];

export const getNoteEventTypeLabel = (eventType) => {
  const match = NOTE_EVENT_TYPES.find((item) => item.value === eventType);
  return match?.label || formatLabel(eventType);
};

export const getNoteDisplayTitle = (note = {}) => {
  if (note.title) return note.title;

  const content = String(note.content || '').trim();
  if (!content) return '-';

  const firstLine = content.split('\n')[0].trim();
  return firstLine.length > 80 ? `${firstLine.slice(0, 80)}...` : firstLine;
};

export const getNotePreview = (note = {}) => {
  const content = String(note.content || '').trim();
  if (!content) return '';

  const normalized = content.replace(/\s+/g, ' ');
  return normalized.length > 120 ? `${normalized.slice(0, 120)}...` : normalized;
};

export const formatNoteDate = (note = {}) => formatDate(note.eventDate || note.createdAt);

export const formatPhoneNumber = (value) => {
  if (!value) return '-';

  const digits = String(value).replaceAll(/\D/g, '');
  const normalizedDigits = digits.length === 11 && digits.startsWith('1') ? digits.substring(1) : digits;

  if (normalizedDigits.length !== 10) {
    return value;
  }

  const areaCode = normalizedDigits.substring(0, 3);
  const prefix = normalizedDigits.substring(3, 6);
  const lineNumber = normalizedDigits.substring(6);

  return `(${areaCode}) ${prefix}-${lineNumber}`;
};

export const getTelHref = (value) => {
  const digits = String(value || '').replaceAll(/\D/g, '');

  if (!digits) {
    return null;
  }

  return `tel:${digits}`;
};

export const getContactDisplayPhone = (contact = {}) => {
  const primaryPhone = String(contact.primaryPhone || '').trim();

  if (primaryPhone) {
    return primaryPhone;
  }

  return String(contact.secondaryPhone || '').trim();
};

export const getCompanyName = (contact = {}) => {
  return (
    contact.company?.name ||
    contact.companyName ||
    contact.organizationName ||
    contact.organization ||
    contact.company?.orgName ||
    '-'
  );
};

export const getCompanySize = (contact = {}) => {
  return contact.company?.companySize || contact.company?.size || contact.companySize || contact.organizationSize || '';
};

export const getInitials = (firstName = '', lastName = '') => {
  const first = (firstName || '').trim().charAt(0);
  const last = (lastName || '').trim().charAt(0);
  const initials = `${first}${last}`.toUpperCase();
  return initials || '?';
};

export const formatSetAsides = (setAsides) => {
  if (!setAsides) return [];

  try {
    const parsed = typeof setAsides === 'string' ? JSON.parse(setAsides) : setAsides;
    if (!Array.isArray(parsed)) return [];

    return parsed
      .map((item) => {
        if (typeof item === 'string') return item;
        return item.code || item.name || item.id || '';
      })
      .filter(Boolean);
  } catch (_error) {
    return [];
  }
};

export const getEditInitialValues = (contact = {}) => {
  const isMonitoring = isMonitoringContactStage(contact.stage);

  return {
    firstName: contact.firstName || '',
    lastName: contact.lastName || '',
    title: contact.title || '',
    email: contact.email || '',
    secondaryEmail: contact.secondaryEmail || '',
    primaryPhone: contact.primaryPhone || '',
    secondaryPhone: contact.secondaryPhone || '',
    contactType: contact.contactType || '',
    stage: contact.stage || DEFAULT_CONTACT_STAGE,
    disposition: isMonitoring ? contact.disposition || '' : contact.disposition || DEFAULT_CONTACT_DISPOSITION,
  };
};

export const contactEditSchema = () =>
  Yup.object().shape({
    firstName: Yup.string().nullable(),
    lastName: Yup.string().nullable(),
    title: Yup.string().nullable(),
    email: Yup.string().email().nullable(),
    secondaryEmail: Yup.string().email().nullable(),
    primaryPhone: Yup.string().nullable(),
    secondaryPhone: Yup.string().nullable(),
    contactType: Yup.string().oneOf(CONTACT_TYPES).nullable(),
    stage: Yup.string().oneOf(CONTACT_STAGES).nullable(),
    disposition: Yup.string().oneOf(CONTACT_DISPOSITIONS).nullable(),
  });

export const ACTIVITY_TYPE_FILTERS = [
  { value: 'contact_added', label: 'Contact Added' },
  { value: 'contact_updated', label: 'Contact Updated' },
  { value: 'contact_deleted', label: 'Contact Deleted' },
  { value: 'note_added', label: 'Note Added' },
  { value: 'note_updated', label: 'Note Updated' },
  { value: 'note_deleted', label: 'Note Deleted' },
  { value: 'company_created', label: 'Company Added' },
  { value: 'company_updated', label: 'Company Updated' },
  { value: 'company_deleted', label: 'Company Deleted' },
];

const ACTIVITY_ICON_TYPES = {
  contact_added: 'added',
  contact_updated: 'updated',
  contact_deleted: 'deleted',
  note_added: 'added',
  note_updated: 'updated',
  note_deleted: 'deleted',
  company_created: 'added',
  company_updated: 'updated',
  company_deleted: 'deleted',
  default: 'default',
};

export const getActivityTypeFromDescription = (description = '') => {
  if (description.startsWith('Created contact')) return 'contact_added';
  if (description.startsWith('Deleted contact')) return 'contact_deleted';
  if (
    description.startsWith('Updated contact') ||
    description.startsWith('Archived contact') ||
    description.startsWith('Unarchived contact')
  ) {
    return 'contact_updated';
  }
  if (description.startsWith('Created company')) return 'company_created';
  if (description.startsWith('Deleted company')) return 'company_deleted';
  if (
    description.startsWith('Updated company') ||
    description.startsWith('Archived company') ||
    description.startsWith('Unarchived company')
  ) {
    return 'company_updated';
  }
  if (description.startsWith('Added a') || description.startsWith('Added an')) return 'note_added';
  if (description.startsWith('Updated a') || description.startsWith('Updated an')) return 'note_updated';
  if (description.startsWith('Deleted a') || description.startsWith('Deleted an')) return 'note_deleted';
  return 'default';
};

export const getActivityDisplay = (description = '') => {
  const type = getActivityTypeFromDescription(description);
  const filter = ACTIVITY_TYPE_FILTERS.find((item) => item.value === type);

  return {
    type,
    title: filter?.label || 'Activity',
    iconType: ACTIVITY_ICON_TYPES[type] || ACTIVITY_ICON_TYPES.default,
  };
};

export const getAuditUserDisplayName = (user) => {
  if (!user) return '';

  const name = [user.name, user.familyName].filter(Boolean).join(' ').trim();
  return name || user.email || '';
};

export const parseAuditMetadata = (metadata) => {
  if (metadata == null || metadata === '') {
    return null;
  }

  if (typeof metadata === 'object') {
    return metadata;
  }

  if (typeof metadata === 'string') {
    try {
      return JSON.parse(metadata);
    } catch (_error) {
      return null;
    }
  }

  return null;
};

export const getAuditDeletedNote = (item) => {
  if (!item) return null;
  const metadata = parseAuditMetadata(item.metadata);
  return metadata?.deletedNote || null;
};

export const canViewActivityNote = (item) => Boolean(item && (item.crmNoteId || getAuditDeletedNote(item)));

const buildAuditChangeFieldLabels = () => {
  const fromColumns = [...ORGANIZATION_LIST_COLUMNS, ...CONTACT_LIST_COLUMNS].reduce((labels, column) => {
    labels[column.id] = column.label;
    return labels;
  }, {});

  return {
    ...fromColumns,
    crmCompanyId: 'organization',
    secondaryEmail: 'alternate-email',
    secondaryPhone: 'alternate-phone',
    contactType: 'type',
    address: 'address',
    zipCode: 'zip-code',
    eventType: 'interaction-type',
    content: 'note-content',
    eventDate: 'date',
    nextStep: 'next-step',
    additionalDetails: 'additional-details',
  };
};

export const AUDIT_CHANGE_FIELD_LABELS = buildAuditChangeFieldLabels();

export const getAuditChanges = (item) => {
  if (!item) return [];
  const metadata = parseAuditMetadata(item.metadata);
  return Array.isArray(metadata?.changes) ? metadata.changes : [];
};

export const canViewActivityChanges = (item) => getAuditChanges(item).length > 0;

export const getAuditChangeFieldLabel = (field, trans) => {
  const labelKey = AUDIT_CHANGE_FIELD_LABELS[field];
  return labelKey ? trans(labelKey) : formatLabel(field);
};

export const formatAuditChangeValue = (value) => {
  if (value == null || value === '') {
    return '-';
  }

  return String(value);
};

export const isContactActivity = (item) => {
  if (!item?.description) return false;
  const type = getActivityTypeFromDescription(item.description);
  return type.startsWith('contact_');
};

export const isCompanyActivity = (item) => {
  if (!item?.description) return false;
  const type = getActivityTypeFromDescription(item.description);
  return type.startsWith('company_');
};
